import { ArrowRight, MapPin } from "lucide-react"
import type { MetaFunction } from "react-router"

import { BlogLayout, BlogProse } from "~/components/blog/BlogLayout"
import { BlogCta } from "~/components/blog/BlogCta"
import { WrittenBy } from "~/components/blog/WrittenBy"
import { H2, Lead, P, Quote, Strong } from "~/components/blog/prose"
import { getPost, postMeta } from "~/data/posts"

// Positioning explainer: what Pine59 means by "Aggregated Location
// Intelligence", and how it differs from device-level data, raw location
// data, and first-party data.

const post = getPost("location-data-vs-location-intelligence")!

// The dot pattern behind both panels — a plain "map" backdrop built from CSS
// alone, no image asset.
const GRID_BACKDROP: React.CSSProperties = {
  backgroundImage:
    "radial-gradient(circle, rgba(92,104,0,0.14) 1px, transparent 1px)",
  backgroundSize: "18px 18px",
}

// Scattered device pings around the shared anchor point (in %). One of them
// is the anchor itself, singled out with the "14:03" timestamp.
const PINGS = [
  { top: "24%", left: "22%" },
  { top: "66%", left: "18%" },
  { top: "20%", left: "70%" },
  { top: "72%", left: "66%" },
  { top: "58%", left: "80%" },
  { top: "78%", left: "40%" },
]
const ANCHOR = { top: "44%", left: "52%" }

// A raw signal cloud for the "location data" side of <DataVsIntelligence> —
// denser near the shopping center at the middle, thinning out toward the
// edges, the way real device pings cluster around a place rather than
// scattering evenly across a map. `size` sets dot scale and opacity so the
// core reads as a hotspot and the outer ring reads as background noise.
const RAW_SIGNALS = [
  // core — clustered tight around the place itself
  { top: "42%", left: "46%", size: "core" },
  { top: "38%", left: "56%", size: "core" },
  { top: "48%", left: "38%", size: "core" },
  { top: "46%", left: "60%", size: "core" },
  { top: "56%", left: "48%", size: "core" },
  { top: "36%", left: "48%", size: "core" },
  { top: "52%", left: "58%", size: "core" },
  { top: "44%", left: "52%", size: "core" },
  // mid ring — still around the place, more spread out
  { top: "26%", left: "34%", size: "mid" },
  { top: "24%", left: "64%", size: "mid" },
  { top: "62%", left: "30%", size: "mid" },
  { top: "66%", left: "68%", size: "mid" },
  { top: "40%", left: "22%", size: "mid" },
  { top: "34%", left: "76%", size: "mid" },
  { top: "60%", left: "58%", size: "mid" },
  { top: "50%", left: "72%", size: "mid" },
  // outer — sparse background signal, far from the place
  { top: "12%", left: "18%", size: "outer" },
  { top: "10%", left: "70%", size: "outer" },
  { top: "84%", left: "20%", size: "outer" },
  { top: "86%", left: "78%", size: "outer" },
  { top: "78%", left: "48%", size: "outer" },
] as const

const SIGNAL_DOT_CLASS: Record<(typeof RAW_SIGNALS)[number]["size"], string> = {
  core: "h-2 w-2 bg-moss/70",
  mid: "h-1.5 w-1.5 bg-moss/45",
  outer: "h-1 w-1 bg-moss/25",
}

/**
 * "Location data vs. location intelligence" — a cloud of raw signals on the
 * left flows into the one trend it adds up to on the right. Same panel
 * language as <ObservationsVsIntelligence>, connected by an arrow instead of
 * shown side by side, since this is about turning volume into a single
 * answer rather than one place seen two ways.
 */
function DataVsIntelligence() {
  return (
    <div
      className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-center"
      role="img"
      aria-label="Diagram showing location data as a cloud of raw location signals around a shopping center, flowing into location intelligence: a single trend, visits up 14% compared with the previous month."
    >
      <div className="w-full max-w-xs">
        <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-line bg-hay/60">
          <div className="absolute inset-0" style={GRID_BACKDROP} aria-hidden />
          {RAW_SIGNALS.map((p, i) => (
            <span
              key={i}
              className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full ${SIGNAL_DOT_CLASS[p.size]}`}
              style={{ top: p.top, left: p.left }}
              aria-hidden
            />
          ))}
        </div>
        <p className="mt-3 text-center font-flex text-[11px] font-medium uppercase tracking-wide text-mute">
          Location data
        </p>
      </div>

      <ArrowRight
        className="h-6 w-6 shrink-0 rotate-90 text-line-cool sm:rotate-0"
        strokeWidth={1.75}
        aria-hidden
      />

      <div className="w-full max-w-xs">
        <div className="relative flex aspect-[4/3] flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-line bg-hay/60">
          <div className="absolute inset-0" style={GRID_BACKDROP} aria-hidden />
          <svg
            viewBox="0 0 80 32"
            className="relative h-8 w-20 text-chartreuse-deep"
            aria-hidden
          >
            <polyline
              points="4,26 18,20 32,22 46,14 60,16 74,6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx={74} cy={6} r={3} fill="currentColor" />
          </svg>
          <div className="relative rounded-lg border border-line bg-paper px-3 py-2 text-center shadow-sm">
            <p className="font-sans text-[16px] font-semibold text-ink">
              +14% visits
            </p>
            <p className="font-flex text-[11px] text-mute">
              vs. previous month
            </p>
          </div>
        </div>
        <p className="mt-3 text-center font-flex text-[11px] font-medium uppercase tracking-wide text-mute">
          Location intelligence
        </p>
      </div>
    </div>
  )
}

/**
 * "Same location shown two ways" — the same store's coordinate rendered
 * left as scattered raw device pings, right as the single aggregated
 * statistic. Built from the design system's own tokens rather than a photo.
 */
function ObservationsVsIntelligence() {
  return (
    <div
      className="mt-10 grid gap-4 sm:grid-cols-2"
      role="img"
      aria-label="Diagram comparing individual device observations for a store — scattered pings, one timestamped 14:03 — to aggregated location intelligence for the same store: a single statistic, 8,420 visits and 27% from trade area."
    >
      <div>
        <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-line bg-hay/60">
          <div className="absolute inset-0" style={GRID_BACKDROP} aria-hidden />
          {PINGS.map((p, i) => (
            <span
              key={i}
              className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-moss/50"
              style={p}
              aria-hidden
            />
          ))}
          <span
            className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-chartreuse-deep ring-4 ring-chartreuse-deep/20"
            style={ANCHOR}
            aria-hidden
          />
          <span
            className="absolute -translate-x-1/2 -translate-y-[calc(100%+10px)] rounded-full bg-forest px-2 py-0.5 font-flex text-[10px] font-medium text-lime"
            style={ANCHOR}
            aria-hidden
          >
            14:03
          </span>
        </div>
        <p className="mt-3 text-center font-flex text-[11px] font-medium uppercase tracking-wide text-mute">
          Individual observations
        </p>
      </div>

      <div>
        <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-line bg-hay/60">
          <div className="absolute inset-0" style={GRID_BACKDROP} aria-hidden />
          <MapPin
            className="absolute -translate-x-1/2 -translate-y-full text-forest"
            style={ANCHOR}
            strokeWidth={1.75}
            aria-hidden
          />
          <div
            className="absolute -translate-x-1/2 translate-y-3 whitespace-nowrap rounded-lg border border-line bg-paper px-3 py-2 text-center shadow-sm"
            style={ANCHOR}
          >
            <p className="font-sans text-[14px] font-semibold text-ink">
              8,420 visits
            </p>
            <p className="font-flex text-[11px] text-mute">
              27% from trade area
            </p>
          </div>
        </div>
        <p className="mt-3 text-center font-flex text-[11px] font-medium uppercase tracking-wide text-mute">
          Aggregated intelligence
        </p>
      </div>
    </div>
  )
}

export const meta: MetaFunction = ({ location }) =>
  postMeta(post, location.pathname)

export default function Post() {
  return (
    <BlogLayout post={post}>
      <BlogProse>
        <WrittenBy post={post} />

        <Lead>
          Location data, location intelligence, device-level data and aggregated
          data are often used interchangeably, but they often describe different
          things. The differences matter, particularly when deciding what kind
          of location data your company actually needs and how much
          responsibility you want to take on for handling it.
        </Lead>
        <P>
          At Pine59, we've chosen to describe what we provide as{" "}
          <Strong>Aggregated Location Intelligence</Strong>.
        </P>
        <P>
          Our products are built from first-party location data that is
          aggregated at the source before it reaches Pine59, meaning neither we
          nor our customers receive the underlying raw device-level data.
        </P>
        <P>
          This lets us build intelligence about how places and populations move,
          change and perform, without needing individual device records.
        </P>
        <P>Here's how we think about the distinction.</P>

        <BlogCta />

        <H2>Aggregated vs. device-level: the most important difference</H2>
        <P>
          If you're choosing a location intelligence provider, one of the first
          questions to ask is whether you actually need device-level data.
        </P>
        <P>
          Device-level location datasets contain observations associated with
          individual devices or persistent identifiers. That doesn't necessarily
          mean the identity of the person carrying the device is known, but it
          does mean individual devices can potentially be distinguished across
          observations.
        </P>
        <P>
          For some applications, that level of granularity is necessary. For
          many common business questions, it isn't. It can also introduce
          additional privacy considerations because your organization is
          handling data associated with individual devices, even when those
          devices aren't directly linked to named individuals.
        </P>
        <P>
          If you want to know how many people visited a store, whether traffic
          is increasing, where visitors come from, how a location performs
          against competitors or who is visiting the area, you're generally
          interested in patterns across groups of people rather than the
          movements of individual devices.
        </P>
        <P>
          Aggregated data changes the unit of analysis. Instead of receiving an
          observation such as{" "}
          <Strong>"Device X was observed at Location Y at 14:03,"</Strong> you
          might receive{" "}
          <Strong>
            "Location Y had an estimated 8,420 visits during 8th of August"
          </Strong>{" "}
          or <Strong>"27% of visitors came from this trade area."</Strong>
        </P>
        <P>
          The underlying signals have been turned into statistics about places
          and populations.{" "}
          <Strong>
            For organizations that don't need device-level information, this can
            provide the intelligence they need while reducing the privacy,
            governance and security considerations that come with receiving and
            handling more granular location data. It also makes the data easier
            to use, since volume increases materially once you are looking at
            every device and all of its observations across a day.
          </Strong>
        </P>

        <ObservationsVsIntelligence />

        <H2>Why we call it Aggregated Location Intelligence</H2>
        <P>
          "Location intelligence" has become a broad category that can describe
          everything from foot traffic analytics and maps to products containing
          device-level observations. That's why Pine59 adds the word{" "}
          <Strong>Aggregated</Strong>.
        </P>
        <P>
          <Strong>
            Aggregated Location Intelligence is location intelligence delivered
            as aggregated statistics about places and populations rather than
            individual device-level observations.
          </Strong>
        </P>
        <P>
          Pine59 builds its products from{" "}
          <Strong>pre-aggregated first-party data</Strong>. The original data is
          collected by our data partners through their own direct relationships,
          systems or infrastructure, and is aggregated before being provided to
          Pine59. We then transform that aggregated data into products that help
          customers understand places and populations.
        </P>
        <P>
          The distinction is important:{" "}
          <Strong>
            neither Pine59 nor our customers receive the underlying device-level
            location data.
          </Strong>
        </P>
        <P>
          For companies that want to understand the physical world without
          needing individual device records, we think that's a fundamentally
          different way to build location intelligence.
        </P>

        <H2>Why does pre-aggregated data matter?</H2>
        <P>
          An organization that needs location intelligence could acquire
          granular device-level data and build the infrastructure and
          methodology required to turn it into useful statistics. Doing so also
          means dealing with the privacy, security, governance, retention and
          regulatory considerations associated with handling granular location
          data.
        </P>
        <P>Pine59 starts further downstream.</P>
        <P>
          Our data partners aggregate their first-party data before it reaches
          us, and it also opens up data sources that are not available in the
          third-party device-level ecosystem. Pine59 then applies its
          technology, methodology and models to turn those aggregated inputs
          into useful location intelligence.
        </P>
        <P>
          This means customers can focus on questions around store performance,
          market potential, trade areas, consumer behavior and competitive
          trends without Pine59 or the customer needing to take possession of
          the underlying device-level data.
        </P>
        <Quote>
          For many use cases, you need the intelligence created from the
          underlying data, not the individual observations themselves.
        </Quote>

        <H2>What can Aggregated Location Intelligence tell you?</H2>
        <P>
          Aggregated Location Intelligence can be used to understand foot
          traffic and visitation trends, trade areas, visitor demographics and
          profiles, competitive performance, movement between places, population
          changes and mobility patterns.
        </P>
        <P>
          A retailer might want to understand why one store is outperforming
          another. A real estate company might evaluate the trade area around a
          potential site. A consultancy might compare markets for a client,
          while a technology company might incorporate visitation trends into
          its own product.
        </P>
        <P>
          These are different use cases, but they have something important in
          common: the questions are about{" "}
          <Strong>places and populations, not individual devices</Strong>.
        </P>

        <H2>Location data vs. location intelligence</H2>
        <P>There's a second distinction worth understanding.</P>
        <P>
          <Strong>Location data</Strong> is the underlying observation, such as
          a signal indicating that a device was observed at a particular
          latitude and longitude at a particular time. Individual observations
          don't tell you very much, but large volumes of them can be processed
          and modeled to reveal patterns.
        </P>
        <P>
          <Strong>Location intelligence</Strong> is the useful information
          produced from those patterns.
        </P>
        <P>
          A collection of location signals around a shopping center is location
          data. An estimate showing that visits to the shopping center increased
          14% compared with the previous month is location intelligence.
        </P>

        <DataVsIntelligence />

        <Quote>
          Location data is the raw material. Location intelligence is what
          you can learn from it.
        </Quote>

        <H2>And what does first-party mean?</H2>
        <P>
          First-party describes{" "}
          <Strong>where the original data comes from</Strong>, while aggregated
          describes <Strong>the form in which Pine59 receives and uses it</Strong>
          .
        </P>
        <P>
          First-party data is collected directly by an organization through its
          own relationships, systems or infrastructure. A retailer's transaction
          data can be first-party data, as can a telecom operator's network data
          or data generated through an app or service.
        </P>
        <P>
          Pine59's model combines these concepts:{" "}
          <Strong>
            our products are built from first-party data that has already been
            aggregated before it reaches us.
          </Strong>
        </P>
        <P>
          A telecom operator, for example, can generate valuable insights from
          its own network data, aggregate those insights within its own
          environment, and provide Pine59 with aggregated data rather than the
          underlying device-level records. Pine59 can then turn those inputs
          into standardized Aggregated Location Intelligence products.
        </P>
        <P>
          So the terminology describes different parts of the process:{" "}
          <Strong>
            first-party describes where the original data comes from,
            device-level versus aggregated describes its granularity, and
            location intelligence describes the useful information created from
            it.
          </Strong>
        </P>

        <H2>Which do you actually need?</H2>
        <P>
          There is no universal answer. If your use case genuinely requires
          individual device-level observations, an aggregated product may not
          provide the granularity you need.
        </P>
        <P>
          But more granular doesn't automatically mean better. It also brings
          additional infrastructure, governance and responsibility, so a useful
          question when choosing a provider is:
        </P>
        <P>
          <Strong>
            What's the least amount of data I need to reliably answer the
            question?
          </Strong>
        </P>
        <P>
          If you're trying to understand patterns about places and populations
          rather than individual devices, you may not need device-level location
          data at all.
        </P>
        <P>
          That's the space Pine59 is built for:{" "}
          <Strong>
            Aggregated Location Intelligence, built from pre-aggregated
            first-party data, without Pine59 or its customers receiving the
            underlying device-level location data.
          </Strong>
        </P>
      </BlogProse>
    </BlogLayout>
  )
}
